import { useState, useRef, useCallback, useEffect } from 'react'
import { sessionsApi } from '../api/sessions.api'
import { useSessionStore } from '../store/sessionStore'

const MAX_RETRIES = 3
const BASE_DELAY_MS = 1000

export function useSSEStream(sessionId: string | null) {
  const [isStreaming, setIsStreaming] = useState(false)
  const esRef = useRef<EventSource | null>(null)
  const retryCountRef = useRef(0)
  const pendingRetryRef = useRef<number | null>(null)
  // La signature d'annulation de l'échange de billet, en cours : incrémentée
  // par `stopStream`, `startStream` et le nettoyage de démontage. `connect`
  // capture sa valeur avant `await getStreamTicket(...)` — devenue ancienne
  // au retour (l'utilisateur a arrêté le flux ou quitté la page pendant
  // l'échange), l'EventSource ne doit jamais s'ouvrir : il serait orphelin
  // (jamais fermé) et continuerait d'écrire dans le store après l'arrêt.
  const generationRef = useRef(0)
  // La reconnexion est récursive : on passe par une ref pour ne pas référencer
  // `connect` avant son initialisation.
  const connectRef = useRef<((content: string, messageUuid: string) => void) | null>(null)

  const { appendStreamingContent, setStreamingMessage, finalizeStreamingMessage } =
    useSessionStore()

  const stopStream = useCallback(() => {
    generationRef.current++
    if (esRef.current) {
      esRef.current.close()
      esRef.current = null
    }
    if (pendingRetryRef.current !== null) {
      window.clearTimeout(pendingRetryRef.current)
      pendingRetryRef.current = null
    }
    setIsStreaming(false)
  }, [])

  const connect = useCallback(
    async (content: string, messageUuid: string) => {
      if (!sessionId) return

      const myGeneration = generationRef.current

      // EventSource ne peut poser aucun en-tête Authorization : le jeton
      // d'API durable ne doit donc jamais transiter par l'URL du flux. On
      // l'échange d'abord (requête normale, authentifiée par en-tête via
      // apiClient) contre un billet à usage unique et de courte durée de
      // vie — c'est ce billet, et lui seul, qui apparaît dans l'URL.
      let ticket: string
      try {
        const res = await sessionsApi.getStreamTicket(sessionId)
        ticket = res.data.data.ticket
      } catch {
        if (myGeneration === generationRef.current) {
          setIsStreaming(false)
          setStreamingMessage(null)
        }
        return
      }

      if (myGeneration !== generationRef.current) {
        // Annulé pendant l'échange (stopStream() ou démontage) : ne jamais
        // ouvrir l'EventSource — il ne serait référencé nulle part, jamais
        // fermé, et continuerait à écrire dans le store après l'arrêt.
        return
      }

      const url = sessionsApi.getStreamUrl(sessionId, content, messageUuid, ticket)
      const es = new EventSource(url)
      esRef.current = es

      es.onmessage = (event: MessageEvent<string>) => {
        if (event.data === '[DONE]') {
          finalizeStreamingMessage()
          stopStream()
          return
        }
        try {
          const parsed = JSON.parse(event.data) as { delta: string }
          appendStreamingContent(parsed.delta)
        } catch {
          // ignore malformed SSE chunks
        }
      }

      es.onerror = () => {
        es.close()
        esRef.current = null
        if (retryCountRef.current < MAX_RETRIES) {
          const delay = BASE_DELAY_MS * Math.pow(2, retryCountRef.current)
          retryCountRef.current++
          pendingRetryRef.current = window.setTimeout(() => {
            pendingRetryRef.current = null
            void connectRef.current?.(content, messageUuid)
          }, delay)
        } else {
          retryCountRef.current = 0
          setIsStreaming(false)
          setStreamingMessage(null)
        }
      }
    },
    [sessionId, appendStreamingContent, setStreamingMessage, finalizeStreamingMessage, stopStream],
  )

  useEffect(() => {
    connectRef.current = connect
  }, [connect])

  const startStream = useCallback(
    (content: string, messageUuid: string) => {
      // Nouvelle génération : annule tout échange de billet resté en vol
      // depuis un appel précédent (démarrage double, ré-appui rapide).
      generationRef.current++
      retryCountRef.current = 0
      setIsStreaming(true)
      setStreamingMessage({ role: 'assistant', content: '', isStreaming: true })
      void connect(content, messageUuid)
    },
    [connect, setStreamingMessage],
  )

  // Cleanup EventSource on unmount
  useEffect(() => {
    return () => {
      // Annule aussi un échange de billet en cours au moment du démontage —
      // sans quoi l'EventSource s'ouvrirait après coup, orpheline, avec un
      // appel IA réel qui continue côté serveur (voir generationRef).
      //
      // `react-hooks/exhaustive-deps` avertit qu'une ref a pu changer d'ici le
      // nettoyage : c'est précisément ce qu'on veut. On n'y lit pas un nœud
      // périmé, on INCRÉMENTE un compteur de génération pour invalider tout
      // travail asynchrone encore en vol. Copier la valeur dans une variable,
      // comme le suggère la règle, invaliderait une génération déjà obsolète et
      // laisserait justement passer le flux orphelin.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      generationRef.current++
      esRef.current?.close()
      if (pendingRetryRef.current !== null) {
        window.clearTimeout(pendingRetryRef.current)
      }
    }
  }, [])

  return { isStreaming, startStream, stopStream }
}
